import {
  Activity,
  Box,
  ChevronRight,
  Plus
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { AGENT_STATUSES_FILTER, AGENT_TYPES_FILTER, FORM_AGENT_STATUSES, FORM_AGENT_TYPES } from '../../../../constants/agents';
import { MOCK_AGENTS } from '../../../../mocks/Agents.mocks';
import { AgentData } from '../../../../types/Agent.types';
import { FilterSelect } from '../../../../components/shared/FilterSelect/FilterSelect';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { TableSearch } from '../../../../components/shared/TableSearch/TableSearch';
import Modal from '../../../../components/shared/Modal/Modal';
import { useToast } from '../../../../hooks/useToast';
import './AgentsView.css';
import { AgentCard } from './components/AgentCard/AgentCard';
import { useHistory } from 'react-router-dom';

export const AgentsView = ({ onMenuOpen = () => { }, menuCloseSignal = 0, selectedAgent: externalSelectedAgent, setSelectedAgent: externalSetSelectedAgent, isFormOpen: externalIsFormOpen, setIsFormOpen: externalSetIsFormOpen }: {
  onMenuOpen?: () => void,
  menuCloseSignal?: number,
  selectedAgent?: AgentData | null,
  setSelectedAgent?: (agent: AgentData | null) => void,
  isFormOpen?: boolean,
  setIsFormOpen?: (open: boolean) => void
} = {}) => {
  const { notify } = useToast();
  const [agents, setAgents] = useState<AgentData[]>(MOCK_AGENTS);
  const [internalSelectedAgent, setInternalSelectedAgent] = useState<AgentData | null>(null);
  const [deleteModalAgent, setDeleteModalAgent] = useState<AgentData | null>(null);
  const [internalIsFormOpen, setInternalIsFormOpen] = useState(false);

  const selectedAgent = externalSelectedAgent !== undefined ? externalSelectedAgent : internalSelectedAgent;
  const setSelectedAgent = externalSetSelectedAgent !== undefined ? externalSetSelectedAgent : setInternalSelectedAgent;

  const isFormOpen = externalIsFormOpen !== undefined ? externalIsFormOpen : internalIsFormOpen;
  const setIsFormOpen = externalSetIsFormOpen !== undefined ? externalSetIsFormOpen : setInternalIsFormOpen;

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const history = useHistory();

  const [formType, setFormType] = useState<string>('autonomous');
  const [formStatus, setFormStatus] = useState<string>('online');

  useEffect(() => {
    if (selectedAgent) {
      setFormType(selectedAgent.type);
      setFormStatus(selectedAgent.status);
    } else {
      setFormType('autonomous');
      setFormStatus('online');
    }
  }, [selectedAgent, isFormOpen]);

  useEffect(() => {
    if (menuCloseSignal > 0) {
      closeLocalMenus();
    }
  }, [menuCloseSignal]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (
        target.closest('.agent-card-action-btn') || 
        target.closest('.agent-card-action-menu') || 
        target.closest('.filter-select-wrapper') ||
        target.closest('.sidebar-form-dropdown') ||
        target.closest('.sidebar-form-select-btn')
      )) {
        return;
      }
      closeLocalMenus();
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const closeLocalMenus = () => {
    setIsTypeFilterOpen(false);
    setIsStatusFilterOpen(false);
    setOpenActionMenuId(null);
  };

  const handleDeleteAgent = (agent: AgentData) => {
    setDeleteModalAgent(agent);
    setOpenActionMenuId(null);
  };

  const confirmDeleteAgent = () => {
    if (!deleteModalAgent) return;
    setAgents(prev => prev.filter(a => a.id !== deleteModalAgent.id));
    if (selectedAgent?.id === deleteModalAgent.id) setSelectedAgent(null);
    setIsFormOpen(false);
    setDeleteModalAgent(null);
    notify('Éxito', 'Agente eliminado exitosamente', 'info');
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || agent.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedAgent || isFormOpen)} drawer>
        <div className="agents-view-container">
          <ModuleHeader title="Agent_Control_Center" subtitle="Administración de agentes autónomos y procesos automatizados." />

          <div className="agents-view-filters-container">
            <div className="agents-view-filters-row">
              <TableSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="BUSCAR AGENTE POR NOMBRE O ID..."
              />

              <div className="agents-view-filter-group">
                <FilterSelect
                  isOpen={isTypeFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsTypeFilterOpen(next);
                  }}
                  onSelect={setTypeFilter}
                  value={typeFilter}
                  options={AGENT_TYPES_FILTER}
                  icon={Box}
                  allLabel="TODOS_LOS_TIPOS"
                />

                <FilterSelect
                  isOpen={isStatusFilterOpen}
                  onToggle={(next) => {
                    closeLocalMenus();
                    onMenuOpen();
                    setIsStatusFilterOpen(next);
                  }}
                  onSelect={setStatusFilter}
                  value={statusFilter}
                  options={AGENT_STATUSES_FILTER}
                  icon={Activity}
                  allLabel="TODOS_LOS_ESTADOS"
                />
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); closeLocalMenus(); onMenuOpen(); setSelectedAgent(null); setIsFormOpen(true); }}
                className="agents-view-new-agent-btn"
              >
                <Plus size={14} />
                Nuevo_Agente
              </button>
            </div>
          </div>

          <div className="agents-view-cards-section">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                isSelected={selectedAgent?.id === agent.id}
                isActionMenuOpen={openActionMenuId === agent.id}
                onSelect={(a) => {
                  setSelectedAgent(a);
                  setIsFormOpen(false);
                }}
                onActionMenuToggle={(id) => {
                  closeLocalMenus();
                  onMenuOpen();
                  setOpenActionMenuId(id);
                }}
                onEdit={(a) => {
                  setSelectedAgent(a);
                  setIsFormOpen(false);
                  setOpenActionMenuId(null);
                }}
                onDelete={(a) => {
                  handleDeleteAgent(a);
                }}
              />
            ))}
          </div>
        </div>

        {/* Agent Form Section */}
        {(selectedAgent || isFormOpen) && (
          <SidebarForm
            drawer
            title={isFormOpen ? 'Nuevo_Registro_Agente' : 'DETALLE DEL AGENTE'}
            onClose={() => { setSelectedAgent(null); setIsFormOpen(false); }}
            submitLabel={isFormOpen ? 'REGISTRAR_AGENTE' : 'EDITAR_AGENTE'}
            onSubmit={() => {
              if (!isFormOpen && selectedAgent) {
                history.push({
                  pathname: `/agents/edit/${selectedAgent.id}`,
                  state: { agent: selectedAgent }
                });
              }
            }}
            showDelete={!isFormOpen}
            onDelete={() => selectedAgent && handleDeleteAgent(selectedAgent)}
            onClick={(e) => e.stopPropagation()}
            metadata={!isFormOpen && selectedAgent ? [
              { label: 'Eficiencia_Media', value: `${selectedAgent.efficiency}%` },
              { label: 'Tareas_Completadas', value: String(selectedAgent.tasksCompleted) }
            ] : undefined}
            metadataTitle="Métricas_Rendimiento"
          >
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Nombre_Agente</label>
              <input
                type="text"
                defaultValue={selectedAgent?.name || ''}
                className="sidebar-form-input"
                placeholder="EJ. AGENTE_LOGISTICA_01"
              />
            </div>

            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Tipo_Agente</label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = openActionMenuId === 'agent-type' ? null : 'agent-type';
                    closeLocalMenus();
                    onMenuOpen();
                    setOpenActionMenuId(next);
                  }}
                  className="sidebar-form-select-btn"
                >
                  <div className="sidebar-form-select-content">
                    {(() => {
                      const currentType = FORM_AGENT_TYPES.find(t => t.value === formType) || FORM_AGENT_TYPES[0];
                      const TypeIcon = currentType.icon;
                      return (
                        <>
                          <TypeIcon size={12} className="sidebar-form-dropdown-item-icon" />
                          <span>{currentType.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <ChevronRight size={10} className={`sidebar-form-select-arrow ${openActionMenuId === 'agent-type' ? "open" : ""}`} />
                </button>

                {openActionMenuId === 'agent-type' && (
                  <div className="sidebar-form-dropdown">
                    {FORM_AGENT_TYPES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setFormType(opt.value); setOpenActionMenuId(null); }}
                        className="sidebar-form-dropdown-item"
                      >
                        <opt.icon size={12} className="sidebar-form-dropdown-item-icon" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado_Operativo</label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = openActionMenuId === 'agent-status' ? null : 'agent-status';
                    closeLocalMenus();
                    onMenuOpen();
                    setOpenActionMenuId(next);
                  }}
                  className="sidebar-form-select-btn"
                >
                  <div className="sidebar-form-select-content">
                    {(() => {
                      const currentStatus = FORM_AGENT_STATUSES.find(t => t.value === formStatus) || FORM_AGENT_STATUSES[0];
                      const StatusIcon = currentStatus.icon;
                      return (
                        <>
                          <StatusIcon size={12} className="sidebar-form-dropdown-item-icon" />
                          <span>{currentStatus.label}</span>
                        </>
                      );
                    })()}
                  </div>
                  <ChevronRight size={10} className={`sidebar-form-select-arrow ${openActionMenuId === 'agent-status' ? "open" : ""}`} />
                </button>

                {openActionMenuId === 'agent-status' && (
                  <div className="sidebar-form-dropdown">
                    {FORM_AGENT_STATUSES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setFormStatus(opt.value); setOpenActionMenuId(null); }}
                        className="sidebar-form-dropdown-item"
                      >
                        <opt.icon size={12} className="sidebar-form-dropdown-item-icon" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Versión_Core</label>
              <input
                type="text"
                defaultValue={selectedAgent?.version || '1.0.0'}
                className="sidebar-form-input"
                placeholder="EJ. 1.2.5"
              />
            </div>
          </SidebarForm>
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deleteModalAgent}
        type="urgent"
        title="Eliminar agente"
        message={`¿Está seguro de que desea eliminar el agente "${deleteModalAgent?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDeleteAgent}
        onCancel={() => setDeleteModalAgent(null)}
      />
    </>
  );
};
